import { apiGet, apiPost } from './client';

// ===== Types =====

export type RespectBand = 'small' | 'medium' | 'spotlight-level';

export interface KudosDTO {
  id: number;
  giver: { id: number; name: string };
  recipient?: { id: number; name: string };
  tag: { id: number; label: string };
  message: string;
  pointValue: number;
  createdAt: string;
}

export interface RespectDTO {
  band: RespectBand;
  rawScore: number;
  perCategoryBreakdown: Record<string, number>;
  behaviourBreakdown: Record<string, number>;
  computedAt: string | null;
  recentKudos: KudosDTO[];
}

export interface SpotlightEntry {
  id: number;
  name: string;
  rawScore: number;
}

export interface CategoryTagDTO {
  id: number;
  label: string;
  weight: number;
  disabledAt: string | null;
}

export interface KudosRecipientDTO {
  id: number;
  name: string;
  email: string;
}

// ===== API functions =====

export async function createKudos(input: {
  toUserId: number;
  categoryTagId: number;
  message: string;
}): Promise<KudosDTO> {
  const res = await apiPost<{ data: KudosDTO }>('/kudos', input);
  return res.data;
}

export async function listKudosForUser(userId: number): Promise<KudosDTO[]> {
  const res = await apiGet<{ data: KudosDTO[] }>(`/users/${userId}/kudos`);
  return res.data;
}

export async function listKudosGivenByUser(userId: number): Promise<KudosDTO[]> {
  const res = await apiGet<{ data: KudosDTO[] }>(`/users/${userId}/kudos/given`);
  return res.data;
}

export async function getRespectForUser(userId: number): Promise<RespectDTO> {
  const res = await apiGet<{ data: RespectDTO }>(`/users/${userId}/respect`);
  return res.data;
}

export async function getSpotlight(limit = 5): Promise<SpotlightEntry[]> {
  const res = await apiGet<{ data: SpotlightEntry[] }>(`/respect/spotlight?limit=${limit}`);
  return res.data;
}

/**
 * GET /api/kudos/category-tags — active tag picker list (disabledAt=null only).
 * Owned by Plan 02 kudosRoutes; Plan 05 is frontend-only.
 */
export async function listKudosCategoryTags(): Promise<CategoryTagDTO[]> {
  const res = await apiGet<{ data: CategoryTagDTO[] }>('/kudos/category-tags');
  return res.data;
}

/**
 * GET /api/kudos/recipients — internal-user directory for the sidebar shortcut
 * (Option C). Server excludes the caller (self-kudos prevention).
 */
export async function listKudosRecipients(): Promise<KudosRecipientDTO[]> {
  const res = await apiGet<{ data: KudosRecipientDTO[] }>('/kudos/recipients');
  return res.data;
}
